import { FormEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PaymentElement, useCheckoutElements } from '@stripe/react-stripe-js/checkout'
import { useNavigate } from '@tanstack/react-router'
import { Loader2Icon } from 'lucide-react'
import { Button } from 'ui/button'
import { Input } from 'ui/input'
import { Label } from 'ui/label'
import { toast } from 'ui/sonner'

import { LinkButton } from '~/components/link-button'
import { Route } from '~/routes/subscription/checkout'
import type { TRoutes } from '~/types/routes'

export const CheckoutForm = () => {
  const checkoutState = useCheckoutElements()
  const [isLoading, setIsLoading] = useState(false)
  const [promotionCode, setPromotionCode] = useState('')
  const [promotionCodeError, setPromotionCodeError] = useState<string | null>(null)
  const [isApplyingPromotionCode, setIsApplyingPromotionCode] = useState(false)
  const [discountedAmountToPay, setDiscountedAmountToPay] = useState<string | null>(null)
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { redirectTo } = Route.useSearch()

  const checkoutSession = checkoutState.type === 'success' ? checkoutState.checkout : null

  const onApplyPromotionCode = async () => {
    if (!checkoutSession || !promotionCode.trim()) return

    setIsApplyingPromotionCode(true)
    setPromotionCodeError(null)

    try {
      const result = await checkoutSession.applyPromotionCode(promotionCode.trim())

      if (result.type === 'error') {
        setPromotionCodeError(result.error.message)
        return
      }

      setDiscountedAmountToPay(result.session.total.total.amount)
      setPromotionCode('')
    } finally {
      setIsApplyingPromotionCode(false)
    }
  }

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!checkoutSession) {
      console.error({ message: 'No checkout session' })
      return
    }

    setIsLoading(true)
    try {
      const checkoutCompleteRoute: TRoutes = '/subscription/checkout/complete'

      const returnUrl = new URL(window.location.origin)
      returnUrl.pathname = checkoutCompleteRoute
      returnUrl.searchParams.set('session_id', checkoutSession.id)
      if (redirectTo) returnUrl.searchParams.set('redirectTo', redirectTo)

      const result = await checkoutSession.confirm({
        returnUrl: returnUrl.toString(),
      })

      if (result.type === 'error') {
        toast.error(result.error.message)
        console.error({ message: 'Error confirming payment', error: result.error })
        return
      }

      if (result.session.status.type === 'complete')
        void navigate({
          to: checkoutCompleteRoute,
          search: {
            session_id: result.session.id,
            redirectTo,
          },
        })
    } catch (error) {
      console.error({ message: 'Error confirming payment', error })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <form className="w-[90vw] max-w-2xl" onSubmit={onSubmit}>
      <div className="rounded-md border border-border bg-card p-3 shadow-lg">
        <div className="mb-4 space-y-2">
          <Label htmlFor="promotion-code">{t('Promo code')}</Label>
          <div className="flex gap-2">
            <Input
              autoComplete="off"
              disabled={checkoutState.type !== 'success' || isApplyingPromotionCode}
              id="promotion-code"
              onChange={event => setPromotionCode(event.target.value)}
              value={promotionCode}
            />
            <Button
              disabled={checkoutState.type !== 'success' || isApplyingPromotionCode || !promotionCode.trim()}
              onClick={() => void onApplyPromotionCode()}
              type="button"
              variant="outline"
            >
              {t('Apply')}
              {isApplyingPromotionCode && <Loader2Icon className="ml-2 size-4 animate-spin" />}
            </Button>
          </div>
          {promotionCodeError && <p className="text-sm text-destructive">{promotionCodeError}</p>}
          {discountedAmountToPay && (
            <p className="text-sm text-muted-foreground">
              {t('Amount to pay')}: {discountedAmountToPay}
            </p>
          )}
        </div>
        <PaymentElement />
      </div>
      <div className="mt-4 flex justify-end gap-4">
        <LinkButton to="/subscription" variant="outline">
          {t('Cancel')}
        </LinkButton>
        <Button disabled={isLoading || !checkoutSession?.canConfirm} type="submit">
          {t('Submit')}
          {isLoading && <Loader2Icon className="ml-2 size-4 animate-spin" />}
        </Button>
      </div>
    </form>
  )
}
